import { useNavigation } from '@react-navigation/native';
import { Field, useFormikContext } from 'formik';
import debounce from 'lodash.debounce';
import React, { ComponentProps, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dimensions, TextInput, FlatList, Keyboard, View } from 'react-native';
import { ProgressBar } from 'react-native-paper';

import ChatImages from '@components/Chat/ChatImages';
import DocumentCard from '@components/Chat/DocumentCard';
import { AddMembersSection } from '@components/Common/AddMembersSection';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { MarginLeftM, MarginRightXs } from '@components/shared/ListSpacers';
import TextField from '@components/shared/TextField/TextField';
import TooltipPopup from '@components/Tutorialization/TooltipPopup';
import { Document, LocalFile, Team } from '@graphql/generated';
import { useChatUploadManager } from '@hooks/useChatUploadManager';
import { useProjectTutorial } from '@hooks/useProjectTutorial';
import {
  TASK_DESC_LENGTH_LIMIT,
  TASK_NAME_LENGTH_LIMIT,
} from '@src/constants/constants';
import theme from '@themes/theme';

import { FormValues } from '../Projects/ProjectFormStack';

type ProjectCreateFormModalProps = {
  textInputRef?: React.RefObject<TextInput>;
  onFormValidityChange?: (isFormBlank: boolean) => void;
  localFiles: LocalFile[];
  setLocalFiles: React.Dispatch<React.SetStateAction<LocalFile[]>>;
  localImages: LocalFile[];
  setLocalImages: React.Dispatch<React.SetStateAction<LocalFile[]>>;
  shouldShowLoader: boolean;
  fileUploadedCount: number;
  isReadySubmitting: boolean;
  selectedTeam?: Team;
  isSubmitting: boolean;
  onDescriptionContentSizeChange?: (height: number) => void;
  /** Rendered height of the description input — defaults to a single line and
   * grows with content (TA-10919 #2). See ProjectCreateBottomSheet's
   * `clampDescriptionFieldHeight`. */
  descriptionFieldHeight?: number;
  /** Reports the name field's bottom offset (from the top of the form) so
   * the parent sheet can size itself to fit the visible fields instead of
   * always resting at a fixed, oversized snap point (TA-10919). */
  onNameFieldBottomChange?: (bottom: number) => void;
} & ComponentProps<typeof AddMembersSection>;

export const ProjectCreateModalForm: React.FC<ProjectCreateFormModalProps> = ({
  onFormValidityChange,
  textInputRef,
  localFiles,
  setLocalFiles,
  localImages,
  setLocalImages,
  shouldShowLoader,
  fileUploadedCount,
  isReadySubmitting,
  selectedTeam,
  isSubmitting,
  onDescriptionContentSizeChange,
  descriptionFieldHeight = 24,
  onNameFieldBottomChange,
  ...addMembersSectionProps
}) => {
  const { t } = useTranslation();
  const {
    isVisible,
    projectTutorialState,
    setProjectTutorialStateAndInStorage,
  } = useProjectTutorial();

  const { values, errors } = useFormikContext<FormValues>();

  const { name } = values;

  const areAllFieldsBlank = () => {
    const { name, description, teamId, files } = values;

    return (
      !name.trim() &&
      !description?.trim() &&
      !teamId &&
      files?.length === 0 &&
      addMembersSectionProps.members?.length === 0
    );
  };

  const saveProjectTutorializationState = async (currentState: string) => {
    setProjectTutorialStateAndInStorage(currentState);
  };

  useEffect(() => {
    const isFormBlank = areAllFieldsBlank();
    onFormValidityChange?.(isFormBlank);
  }, [values, errors]);

  const { description } = values;

  useEffect(() => {
    if (projectTutorialState && isVisible) {
      if (description && description?.length > 0) {
        if (Number(projectTutorialState) > 3) {
          saveProjectTutorializationState(projectTutorialState?.toString());
          return;
        }
      }
    }
  }, [description?.length, projectTutorialState, isVisible]);

  const navigation = useNavigation();

  const isMixedAttachment = localImages?.length > 0 && localFiles?.length > 0;
  const { removeUploadingFile } = useChatUploadManager();

  const removeLocalFile = async (file: LocalFile) => {
    Keyboard.dismiss();
    const newLocalFiles = localFiles.filter((i) => i.id !== file.id);

    setLocalFiles(newLocalFiles);
    removeUploadingFile(file);
  };

  const removeLocalImage = async (file: LocalFile) => {
    Keyboard.dismiss();
    const newLocalImages = localImages.filter((i) => i.id !== file.id);

    setLocalImages(newLocalImages);
    removeUploadingFile(file);
  };

  const handleStoppedTyping = useCallback(
    debounce(() => {
      saveProjectTutorializationState('4');
    }, 800),
    []
  );

  const [showTutorialTooltip, setShowTutorialTooltip] = React.useState(false);
  React.useEffect(() => {
    const timer = setTimeout(() => setShowTutorialTooltip(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const [nameFieldBottom, setNameFieldBottom] = React.useState<number | null>(
    null
  );
  const [descFieldTop, setDescFieldTop] = React.useState<number | null>(null);
  // Offset by xxl (48px) to clear the label/placeholder row inside the description field.
  // Using y+height is wrong here because the field has minHeight:200 which pushes the
  // tooltip far below the placeholder text.
  const descTooltipTop =
    descFieldTop !== null ? descFieldTop + theme.spacing.xxl : null;

  return (
    <Box flex={1}>
      <View style={{ flex: 1 }}>
        <Box>
          <Box flex={1} marginHorizontal='m'>
            {/* Title Field (+ Location Field, when set, TA-10919 Copilot
                review #5939 fix — both live inside this measured View so
                the location row's height counts toward nameFieldBottom;
                it previously rendered as a sibling below it and was
                silently dropped from the sheet's fit-height calc). */}
            <View
              testID='project-create-name-field-bottom'
              onLayout={(e) => {
                const bottom =
                  e.nativeEvent.layout.y + e.nativeEvent.layout.height;
                setNameFieldBottom(bottom);
                onNameFieldBottomChange?.(bottom);
              }}>
              <Field
                component={TextField}
                label={t('models:projects.placeholders.projectName')}
                name='name'
                accessibilityLabel={t(
                  'models:projects.placeholders.projectName'
                )}
                editable={!isSubmitting && !isReadySubmitting}
                hideError={
                  projectTutorialState === '3' ||
                  selectedTeam?.hasReachedMaxProjects
                }
                isCreateTask
                isRequired
                borderless
                isLargeText
                showCharsRemaining={name?.length > 0}
                textInputProps={{
                  fontFamily: 'Inter_400Regular',
                  fontSize: 16,
                  color: isReadySubmitting
                    ? theme.colors.grey04
                    : theme.colors.textPrimary,
                  maxLength: TASK_NAME_LENGTH_LIMIT,
                  ref: textInputRef,
                  multiline: false,
                  numberOfLines: 1,
                  scrollEnabled: true,
                }}
                marginBottom='none'
                autoFocus
                useBottomSheetInput
                placeholder={t('models:projects.placeholders.name')}
              />

              {/* Location Field - Only visible when location is set */}
              {values?.location?.address && (
                <Box flexDirection='row' alignItems='center' marginBottom='m'>
                  <Icon name='MapPin' color='textSecondary' variant='s' />
                  <Text
                    variant='webBodyHintSubline'
                    color='textSecondary'
                    marginLeft='xs'
                    numberOfLines={1}
                    style={{ flex: 1 }}>
                    {values?.location.address}
                  </Text>
                </Box>
              )}
            </View>

            {/* Description Field */}
            <Box
              style={{ minHeight: descriptionFieldHeight }}
              onLayout={(e) => setDescFieldTop(e.nativeEvent.layout.y)}>
              <Field name='description'>
                {({ field, form, ...props }: any) => (
                  <TextField
                    {...props}
                    field={field}
                    form={form}
                    onChangeText={handleStoppedTyping}
                    label={t('models:projects.placeholders.projectDescription')}
                    borderless
                    editable={!isSubmitting && !isReadySubmitting}
                    isCreateTask
                    marginBottom='none'
                    showCharsRemaining
                    isRemainingTextAtTop
                    useBottomSheetInput
                    textInputProps={{
                      maxLength: TASK_DESC_LENGTH_LIMIT,
                      color: isReadySubmitting
                        ? theme.colors.grey04
                        : theme.colors.textPrimary,
                      multiline: true,
                      scrollEnabled: false,
                      style: {
                        // Explicit `height` (not `minHeight`) — TextField's
                        // own multiline style sets a fixed `height: 24` that
                        // otherwise wins over a `minHeight` here and locks
                        // the field at one line forever (TA-10919 #2). This
                        // `multiline` only reaches TextField via
                        // `textInputProps`, not as its top-level prop, so
                        // TextField's own paddingTop-for-multiline branch
                        // never fires. A previous round added an explicit
                        // `paddingTop` here to push text down into the extra
                        // spacing.s of height, but that clipped capital-letter
                        // ascenders at the top instead — the box just wasn't
                        // tall enough for both the padding and a full line
                        // (TA-10919 QA round 7). Leaving the extra spacing.s as
                        // unclaimed height below the text instead, matching
                        // TaskCreateModalFormNew's own description field,
                        // which reserves the same extra height without a
                        // matching paddingTop.
                        height: descriptionFieldHeight + theme.spacing.s,
                        textAlignVertical: 'top',
                      },
                    }}
                    placeholder={t('models:projects.placeholders.description')}
                    accessibilityLabel={t(
                      'models:projects.placeholders.projectDescription'
                    )}
                  />
                )}
              </Field>
            </Box>

            {showTutorialTooltip &&
              isVisible &&
              projectTutorialState === '3' &&
              nameFieldBottom !== null &&
              !name && (
                <Box
                  position='absolute'
                  top={nameFieldBottom}
                  left={0}
                  right={0}
                  zIndex={999}>
                  <TooltipPopup
                    visible={true}
                    title={t('models:projects.tutorial.create.name.title')}
                    description={t(
                      'models:projects.tutorial.create.name.description'
                    )}
                    pointerPosition='top-left'
                    currentStep={1}
                    totalSteps={4}
                    maxWidth={Dimensions.get('window').width - theme.spacing.xl}
                    topLeftPos={theme.spacing.m}
                  />
                </Box>
              )}

            {showTutorialTooltip &&
              isVisible &&
              projectTutorialState === '3' &&
              descTooltipTop !== null &&
              name &&
              !(description?.length ?? 0) && (
                <Box
                  position='absolute'
                  top={descTooltipTop}
                  left={0}
                  right={0}
                  zIndex={999}>
                  <TooltipPopup
                    visible={true}
                    title={t(
                      'models:projects.tutorial.create.description.title'
                    )}
                    description={t(
                      'models:projects.tutorial.create.description.description'
                    )}
                    pointerPosition='top-left'
                    onSkip={() => setProjectTutorialStateAndInStorage('4')}
                    currentStep={2}
                    totalSteps={4}
                    maxWidth={Dimensions.get('window').width - theme.spacing.xl}
                    topLeftPos={theme.spacing.m}
                  />
                </Box>
              )}
          </Box>

          <Box
            position='absolute'
            opacity={0}
            pointerEvents='none'
            style={{
              left: theme.spacing.m,
              right: theme.spacing.m,
              bottom: theme.spacing.s,
            }}
            onLayout={(e) => {
              onDescriptionContentSizeChange?.(e.nativeEvent.layout.height);
            }}>
            <Text>
              {(values.description?.length ? values.description : ' ') + '\n'}
            </Text>
          </Box>

          {shouldShowLoader && localFiles?.length + localImages?.length > 0 && (
            <Box marginHorizontal='m' bg='grey01' padding='m'>
              <Text fontSize={14} fontWeight='500'>
                {`Uploading ${fileUploadedCount}/${
                  localFiles?.length + localImages?.length
                } files`}{' '}
              </Text>
              <ProgressBar
                color={theme.colors.greenSecondary}
                style={{
                  marginTop: 4,
                  borderRadius: 4,
                  height: 8,
                  backgroundColor: theme.colors.grey02,
                }}
                visible={shouldShowLoader}
                progress={
                  fileUploadedCount / (localFiles?.length + localImages?.length)
                }
              />
            </Box>
          )}

          {!isReadySubmitting &&
            !shouldShowLoader &&
            localImages?.length > 0 && (
              <Box
                borderWidth={isMixedAttachment ? 1 : 0}
                marginHorizontal='m'
                marginBottom={isMixedAttachment ? 'xs' : 'm'}
                paddingHorizontal={isMixedAttachment ? 'xs' : 'none'}
                borderRadius='xs'
                borderColor='grey02'>
                <ChatImages
                  numColumns={6}
                  hideSuccessIcon
                  onDelete={removeLocalImage}
                  list={localImages}
                  onPress={(image: Document | LocalFile) => {
                    navigation.navigate('chat-composer-image-preview', {
                      images: localImages,
                      initialIndex: localImages.findIndex(
                        (img) => img.id === image.id
                      ),
                      onDelete: removeLocalImage,
                    });
                  }}
                />
              </Box>
            )}

          {!isReadySubmitting &&
            !shouldShowLoader &&
            localFiles?.length > 0 && (
              <Box
                mt='xs'
                borderWidth={isMixedAttachment ? 1 : 0}
                borderColor='grey02'
                borderRadius='xs'
                marginHorizontal='m'
                marginBottom='m'>
                <FlatList
                  keyboardShouldPersistTaps='always'
                  data={localFiles}
                  scrollEnabled={false}
                  keyExtractor={(item) => item.id}
                  renderItem={({ item }) => (
                    <Box
                      style={{
                        marginTop: theme.spacing.xxs,
                      }}>
                      <DocumentCard
                        filterVal=''
                        showBorder={false}
                        document={{ ...item }}
                        onDelete={removeLocalFile}
                      />
                    </Box>
                  )}
                  ListFooterComponent={MarginRightXs}
                  ListHeaderComponent={MarginLeftM}
                />
              </Box>
            )}
        </Box>
      </View>
    </Box>
  );
};
